import React from "react";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { CARD, useIsDark, seriesColors } from "./ui";
import { changeTone, formatChange } from "../analyticsUtils";

interface Props {
  label: string;
  value: string;
  change?: number | null;
  /** false for rates that should fall (no-shows, discounts). */
  higherIsBetter?: boolean;
  /** true when a move either way is just information (supplier spend). */
  neutral?: boolean;
  sparkline?: Array<number | null>;
  hint?: string;
  onClick?: () => void;
}

const TONE = {
  good: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400",
  bad: "bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400",
  neutral: "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400",
};

function Sparkline({ points, color }: { points: Array<number | null>; color: string }) {
  const nums = points.filter((p): p is number => typeof p === "number" && Number.isFinite(p));
  if (nums.length < 2) return null;
  const w = 96;
  const h = 28;
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const span = max - min || 1;
  const step = w / Math.max(1, points.length - 1);
  const d = points
    .map((p, i) => (typeof p === "number" && Number.isFinite(p) ? `${i * step},${h - 2 - ((p - min) / span) * (h - 4)}` : null))
    .filter(Boolean)
    .join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" className="shrink-0">
      <polyline points={d} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export default function KpiCard({ label, value, change, higherIsBetter = true, neutral = false, sparkline, hint, onClick }: Props) {
  const isDark = useIsDark();
  const tone = neutral ? "neutral" : changeTone(change, higherIsBetter);
  const Arrow = typeof change === "number" && change < 0 ? ArrowDownwardIcon : ArrowUpwardIcon;
  const body = (
    <>
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</p>
      <div className="flex items-end justify-between gap-2 mt-1">
        <p className="text-2xl font-bold text-slate-800 dark:text-slate-100 tabular-nums leading-tight">{value}</p>
        {sparkline && <Sparkline points={sparkline} color={seriesColors(isDark)[0]} />}
      </div>
      <div className="flex items-center gap-2 mt-2 min-h-[20px]">
        {change !== undefined && (
          <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${TONE[tone]}`}>
            {typeof change === "number" && change !== 0 && <Arrow sx={{ fontSize: 12 }} />}
            {formatChange(change)}
          </span>
        )}
        <span className="text-[11px] text-slate-400 dark:text-slate-500">{hint ?? (change !== undefined ? "vs previous period" : "")}</span>
      </div>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${CARD} p-4 text-left w-full hover:shadow-xl hover:border-primary/30 dark:hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/30`}
      >
        {body}
      </button>
    );
  }
  return <div className={`${CARD} p-4`}>{body}</div>;
}
